import { useEffect, useState } from "react";

/** Presentation timestamps match decoded-frame cache keys, including variable-frame-rate media. */
export function usePresentedFrameTime(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  src: string | undefined,
  subscribeToTime?: (callback: (time: number) => void) => () => void,
) {
  const [time, setTime] = useState(0);
  useEffect(() => {
    let active = true;
    let frameId: number | undefined;
    let video: HTMLVideoElement | null = null;
    let unsubscribe: (() => void) | undefined;
    const updateFallbackTime = (time: number) => {
      // A seek target can advance ahead of the decoded preview. Keep the last
      // displayed position until the browser finishes seeking.
      if (video && active && !video.seeking) setTime(time);
    };
    const updateMediaTime = () => {
      if (video) updateFallbackTime(video.currentTime);
    };
    const frame = (_now: number, metadata: VideoFrameCallbackMetadata) => {
      if (!active || !video) return;
      setTime(metadata.mediaTime);
      frameId = video.requestVideoFrameCallback(frame);
    };
    const attach = () => {
      video = videoRef.current;
      if (!video) return false;
      updateMediaTime();
      if (typeof video.requestVideoFrameCallback === "function") {
        // Keep this armed while paused/seeking, too. Only frames actually sent
        // to the compositor may advance the overlay; seeked/currentTime can
        // describe a newer request, or a time inside the same displayed frame.
        frameId = video.requestVideoFrameCallback(frame);
      } else {
        video.addEventListener("seeked", updateMediaTime);
        video.addEventListener("loadeddata", updateMediaTime);
        video.addEventListener("timeupdate", updateMediaTime);
        unsubscribe = subscribeToTime?.(updateFallbackTime);
      }
      return true;
    };
    const retry = attach()
      ? undefined
      : setInterval(() => {
          if (attach()) clearInterval(retry);
        }, 100);
    return () => {
      active = false;
      clearInterval(retry);
      unsubscribe?.();
      if (video) {
        if (frameId !== undefined) video.cancelVideoFrameCallback(frameId);
        video.removeEventListener("seeked", updateMediaTime);
        video.removeEventListener("loadeddata", updateMediaTime);
        video.removeEventListener("timeupdate", updateMediaTime);
      }
    };
  }, [videoRef, src, subscribeToTime]);
  return time;
}
